/* eslint-disable max-statements, complexity */
import React, { useState, useRef, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import Modal from "../modal";
import styles from "./styles.css";
import { validateEmail } from "../../../utils/validators/email-validator";
import { mobileNumberValidatorAU } from "../../../utils/validators/mobile-number-validator";
import umsService, { OTP_METHOD } from "../../../service/ums-service";

const RESEND_TIMER_SECONDS = 30;
// eslint-disable-next-line no-magic-numbers
const TIMER_INTERVAL_MS = 1000;

const formatPhoneForOtp = (value) => {
    const phone = value.replace(/[\s-]/g, "");
    if (phone.startsWith("+")) return phone;
    if (phone.startsWith("0")) return `+61${phone.slice(1)}`;
    return `+61${phone}`;
};

const EditProfileModal = ({
    isOpen,
    onClose,
    profileName = "",
    email = "",
    mobile = "",
    primaryIdentifierType = "phone",
    phoneNumberVerified = false,
    emailVerified = false,
    secureToken,
    setProfileNameConnect,
    setEmailConnect,
    setProfileMobileConnect
}) => {
    const isEmailSecondary = primaryIdentifierType === "phone";

    const [name, setName] = useState(profileName || "");
    const [secondaryValue, setSecondaryValue] = useState("");
    const [nameError, setNameError] = useState("");
    const [secondaryError, setSecondaryError] = useState("");
    const [globalError, setGlobalError] = useState("");

    // OTP flow state
    const [otpSent, setOtpSent] = useState(false);
    const [otpValue, setOtpValue] = useState("");
    const [otpVerified, setOtpVerified] = useState(false);
    const [sendingOtp, setSendingOtp] = useState(false);
    const [verifyingOtp, setVerifyingOtp] = useState(false);
    const [resendTimer, setResendTimer] = useState(0);
    const [saving, setSaving] = useState(false);

    const timerRef = useRef(null);

    const primaryValue = isEmailSecondary ? mobile : email;
    const secondaryLabel = isEmailSecondary ? "Email address" : "Phone number";
    const secondaryPlaceholder = isEmailSecondary ? "yourname@email.com" : "04XX XXX XXX";
    const secondaryInputType = isEmailSecondary ? "email" : "tel";
    const currentSecondary = isEmailSecondary ? email : mobile;
    const secondaryChanged = secondaryValue.trim() !== (currentSecondary || "");
    const isSecondaryVerified = isEmailSecondary ? emailVerified : phoneNumberVerified;
    const secondaryExists = !!currentSecondary;
    // Fresh add or value changed → UPDATE flow; existing unverified unchanged → VERIFY flow
    const isFreshOrUpdate = !secondaryExists || secondaryChanged;
    const needsOtpForSecondary = (secondaryChanged || (secondaryExists && !isSecondaryVerified)) && !otpVerified;

    useEffect(() => {
        if (isOpen) {
            setName(profileName || "");
            setSecondaryValue(currentSecondary || "");
            setNameError("");
            setSecondaryError("");
            setGlobalError("");
            setOtpSent(false);
            setOtpValue("");
            setOtpVerified(false);
            setSendingOtp(false);
            setVerifyingOtp(false);
            setResendTimer(0);
            setSaving(false);
        }
        return () => {
            if (timerRef.current) window.clearInterval(timerRef.current);
        };
    }, [isOpen, profileName, currentSecondary]);

    const startResendTimer = useCallback(() => {
        setResendTimer(RESEND_TIMER_SECONDS);
        if (timerRef.current) window.clearInterval(timerRef.current);
        timerRef.current = window.setInterval(() => {
            setResendTimer((prev) => {
                if (prev <= 1) {
                    window.clearInterval(timerRef.current);
                    timerRef.current = null;
                    return 0;
                }
                return prev - 1;
            });
        }, TIMER_INTERVAL_MS);
    }, []);

    const validateSecondary = (val) => {
        if (!val.trim()) {
            return isEmailSecondary ? "Email is required" : "Phone number is required";
        }
        if (isEmailSecondary && !validateEmail(val.trim())) {
            return "Please enter a valid email address";
        }
        if (!isEmailSecondary && !mobileNumberValidatorAU(val.trim())) {
            return "Please enter a valid AU mobile number";
        }
        return "";
    };

    const getOtpMethodId = () => {
        if (isFreshOrUpdate) {
            return isEmailSecondary ? OTP_METHOD.UPDATE_EMAIL : OTP_METHOD.UPDATE_PHONE;
        }
        return isEmailSecondary ? OTP_METHOD.EMAIL_VERIFY : OTP_METHOD.PHONE_VERIFY;
    };

    const handleSendOtp = async () => {
        const validationError = validateSecondary(secondaryValue);
        if (validationError) {
            setSecondaryError(validationError);
            return;
        }
        setSecondaryError("");
        setSendingOtp(true);
        setGlobalError("");
        try {
            const methodId = getOtpMethodId();
            const identifier = isEmailSecondary ? secondaryValue.trim() : formatPhoneForOtp(secondaryValue.trim());
            await umsService.generateOtp(secureToken, methodId, identifier);
            setOtpSent(true);
            setOtpVerified(false);
            setOtpValue("");
            startResendTimer();
        } catch (sendErr) {
            setSecondaryError(sendErr?.message || "Failed to send OTP. Please try again.");
        } finally {
            setSendingOtp(false);
        }
    };

    const handleVerifyOtp = async () => {
        if (!otpValue.trim()) return;
        setVerifyingOtp(true);
        setSecondaryError("");
        setGlobalError("");
        try {
            if (isFreshOrUpdate) {
                // New contact or changed value — PATCH /user/phone or /user/email
                if (isEmailSecondary) {
                    await umsService.updateEmail(secureToken, secondaryValue.trim(), otpValue.trim());
                } else {
                    await umsService.updatePhone(secureToken, formatPhoneForOtp(secondaryValue.trim()), otpValue.trim());
                }
            } else {
                // Verify existing unverified — POST /user/contact/verify
                const verifyType = isEmailSecondary ? OTP_METHOD.EMAIL_VERIFY : OTP_METHOD.PHONE_VERIFY;
                await umsService.verifyContact(secureToken, verifyType, otpValue.trim());
            }
            setOtpVerified(true);
            setOtpSent(false);
            if (timerRef.current) {
                window.clearInterval(timerRef.current);
                timerRef.current = null;
            }
            setResendTimer(0);
            if (isEmailSecondary && setEmailConnect) {
                setEmailConnect(secondaryValue.trim());
            } else if (!isEmailSecondary && setProfileMobileConnect) {
                setProfileMobileConnect(secondaryValue.trim());
            }
        } catch (verifyErr) {
            setSecondaryError(verifyErr?.message || "Invalid OTP. Please try again.");
        } finally {
            setVerifyingOtp(false);
        }
    };

    const handleResend = async () => {
        if (resendTimer > 0) return;
        setSendingOtp(true);
        setSecondaryError("");
        try {
            const methodId = getOtpMethodId();
            const identifier = isEmailSecondary ? secondaryValue.trim() : formatPhoneForOtp(secondaryValue.trim());
            await umsService.generateOtp(secureToken, methodId, identifier);
            startResendTimer();
        } catch (resendErr) {
            setSecondaryError(resendErr?.message || "Failed to resend OTP.");
        } finally {
            setSendingOtp(false);
        }
    };

    const handleSave = async () => {
        let hasError = false;
        setGlobalError("");

        if (!name.trim()) {
            setNameError("Name is required");
            hasError = true;
        } else {
            setNameError("");
        }

        if (hasError) return;

        const nameChanged = name.trim() !== (profileName || "");
        if (!nameChanged) {
            onClose();
            return;
        }

        setSaving(true);
        try {
            const parts = name.trim().split(/\s+/);
            await umsService.updateUserProfile(secureToken, {
                first_name: parts[0] || "",
                last_name: parts.slice(1).join(" ") || ""
            });
            if (setProfileNameConnect) setProfileNameConnect(name.trim());
            onClose();
        } catch (saveErr) {
            setGlobalError(saveErr?.message || "Failed to save. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            close={onClose}
            contentClass={styles.modalContent}
            overlayClass={styles.modalOverlay}
            shouldCloseOnOverlayClick={true}
        >
            <div>
                <div className={styles.header}>
                    <button className={styles.backButton} onClick={onClose}>
                        &#8592;
                    </button>
                    <h2 className={styles.title}>Edit Profile</h2>
                </div>

                {globalError && <p className={styles.globalError}>{globalError}</p>}

                <div className={styles.fieldGroup}>
                    <label className={styles.label}>Display name</label>
                    <input
                        type="text"
                        className={`${styles.input} ${nameError ? styles.inputError : ""}`}
                        placeholder="Your Name"
                        value={name}
                        onChange={(e) => {
                            setName(e.target.value);
                            if (nameError) setNameError("");
                        }}
                    />
                    {nameError && <span className={styles.errorText}>{nameError}</span>}
                </div>

                <div className={styles.fieldGroup}>
                    <div className={styles.labelRow}>
                        <label className={styles.label} style={{marginBottom: 0}}>
                            {isEmailSecondary ? "Phone number" : "Email address"}
                        </label>
                        <span className={styles.verifiedBadge}>&#10003; Verified</span>
                    </div>
                    <input
                        type="text"
                        className={`${styles.input} ${styles.inputDisabled}`}
                        value={primaryValue || ""}
                        disabled
                    />
                </div>

                <div className={styles.fieldGroup}>
                    <div className={styles.labelRow}>
                        <label className={styles.label} style={{marginBottom: 0}}>{secondaryLabel}</label>
                        {(isSecondaryVerified || otpVerified) && (
                            <span className={styles.verifiedBadge}>&#10003; Verified</span>
                        )}
                    </div>
                    <input
                        type={secondaryInputType}
                        className={`${styles.input} ${secondaryError ? styles.inputError : ""} ${isSecondaryVerified ? styles.inputDisabled : ""}`}
                        placeholder={secondaryPlaceholder}
                        value={secondaryValue}
                        disabled={isSecondaryVerified || otpSent}
                        onChange={(e) => {
                            setSecondaryValue(e.target.value);
                            if (secondaryError) setSecondaryError("");
                            setOtpVerified(false);
                        }}
                    />
                    {secondaryError && <span className={styles.errorText}>{secondaryError}</span>}

                    {needsOtpForSecondary && !otpSent && (
                        <button
                            className={styles.sendOtpButton}
                            onClick={handleSendOtp}
                            disabled={sendingOtp}
                            style={{marginTop: 8}}
                        >
                            {sendingOtp && "Sending..."}
                            {!sendingOtp && isFreshOrUpdate && "Send OTP"}
                            {!sendingOtp && !isFreshOrUpdate && "Verify"}
                        </button>
                    )}

                    {otpSent && (
                        <React.Fragment>
                            <p className={styles.otpSentText}>OTP sent to {secondaryValue}</p>
                            <div className={styles.otpRow}>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    className={styles.otpInput}
                                    placeholder="Enter OTP"
                                    value={otpValue}
                                    onChange={(e) => setOtpValue(e.target.value)}
                                    maxLength="6"
                                    autoFocus
                                    onKeyDown={(e) => e.key === "Enter" && handleVerifyOtp()}
                                />
                                <button
                                    className={styles.sendOtpButton}
                                    onClick={handleVerifyOtp}
                                    disabled={verifyingOtp || !otpValue.trim()}
                                >
                                    {verifyingOtp ? "Verifying..." : "Verify"}
                                </button>
                            </div>
                            <div className={styles.resendText}>
                                {resendTimer > 0 ? (
                                    <span>Resend OTP in {resendTimer}s</span>
                                ) : (
                                    <button
                                        className={styles.resendLink}
                                        onClick={handleResend}
                                        disabled={sendingOtp}
                                    >
                                        Resend OTP
                                    </button>
                                )}
                            </div>
                        </React.Fragment>
                    )}
                </div>

                <button
                    className={styles.submitButton}
                    onClick={handleSave}
                    disabled={saving || otpSent}
                >
                    {saving ? "Saving..." : "Save changes"}
                </button>
            </div>
        </Modal>
    );
};

EditProfileModal.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    profileName: PropTypes.string,
    email: PropTypes.string,
    mobile: PropTypes.string,
    primaryIdentifierType: PropTypes.string,
    phoneNumberVerified: PropTypes.bool,
    emailVerified: PropTypes.bool,
    secureToken: PropTypes.string,
    setProfileNameConnect: PropTypes.func,
    setEmailConnect: PropTypes.func,
    setProfileMobileConnect: PropTypes.func
};

export default EditProfileModal;
